/**
 * ============================================
 * SELECTOR DE CARRITO - UTILIDAD REUTILIZABLE
 * ============================================
 * Maneja la selección de opciones y actualización de atributos del carrito
 * ✨ VERSIÓN MEJORADA con persistencia y sincronización
 */

class CartSelector {
  constructor(options) {
    this.containerId = options.containerId;
    this.items = options.items;
    this.attributeName = options.attributeName;
    this.onSelect = options.onSelect || null;
    this.storageKey = `cart_selector_${this.attributeName}`;
    
    // Inicializar selección desde: 1) localStorage, 2) default
    this.selectedItem = this.loadSelection();
  }

  /**
   * Carga la selección desde localStorage o default
   */
  loadSelection() {
    // 1. Intentar desde localStorage
    const storedId = localStorage.getItem(this.storageKey);
    if (storedId) {
      const storedItem = this.items.find(item => item.id === storedId);
      if (storedItem) {
        console.log(`📦 Selección cargada desde localStorage (${this.attributeName}): ${storedId}`);
        return storedItem;
      }
    }

    // 2. Usar default o primer elemento
    const defaultItem = this.items.find(item => item.default) || this.items[0];
    console.log(`⭐ Usando selección por defecto (${this.attributeName}): ${defaultItem.id}`);
    return defaultItem;
  }

  /**
   * Renderiza las opciones en el contenedor
   * 🔧 MEJORADO: Fuerza la sincronización del estado del radio button
   */
  render() {
    const container = document.getElementById(this.containerId);
    if (!container) {
      console.error(`Container con ID "${this.containerId}" no encontrado`);
      return;
    }

    container.innerHTML = "";

    this.items.forEach(item => {
      const isSelected = this.isSelected(item);
      const label = document.createElement("label");
      label.className = "cart-selector-option" + (isSelected ? " selected" : "");

      label.innerHTML = `
        <input type="radio" name="${this.attributeName}" value="${item.id}" tabindex="-1"
          ${isSelected ? "checked" : ""}>
        <div class="cart-selector-icon">${item.icon}</div>
        <div class="cart-selector-info">
          <div class="cart-selector-name">
            ${item.name}
            ${item.badge ? `<span class="cart-selector-badge">${item.badge}</span>` : ""}
          </div>
          ${item.description ? `<div class="cart-selector-description">${item.description}</div>` : ""}
          ${item.cost ? `<div class="cart-selector-cost">${item.cost}</div>` : ""}
        </div>
      `;

      const radioInput = label.querySelector('input[type="radio"]');

      // 🔧 FIX SAFARI/iOS: el salto de scroll lo causa el propio gesto de tap
      // sobre el radio, que WebKit usa para decidir el auto-scroll-into-view
      // ANTES de que dispare "change" — por lo que hacer blur() después no
      // alcanza a evitarlo. La solución es no dejar que el tap le dé foco al
      // input: interceptamos el "click" en fase de captura, prevenimos el
      // foco nativo y marcamos "checked" manualmente por JS.
      label.addEventListener("click", (e) => {
        e.preventDefault();
        if (this.isSelected(item)) return;
        radioInput.checked = true;
        this.selectItem(item, label);
      });

      container.appendChild(label);
    });

    console.log(`✅ Selector renderizado (${this.attributeName}): ${this.selectedItem.id} seleccionado`);
  }

  /**
   * Verifica si un item está seleccionado
   */
  isSelected(item) {
    return item.id === this.selectedItem.id;
  }

  /**
   * Selecciona un item y actualiza el carrito
   * 🔧 MEJORADO: Guarda en localStorage
   * 🔧 FIX MOBILE: preserva la posición de scroll. En mobile, reconstruir el DOM
   * del input enfocado (o insertar/ocultar contenido debajo, como el botón
   * "Proceder a compra") hace que el navegador re-centre el foco y arrastre
   * la pantalla hacia el pie de página.
   */
  selectItem(item, labelElement) {
    this.selectedItem = item;
    const scrollY = window.scrollY;

    // 💾 Guardar en localStorage
    localStorage.setItem(this.storageKey, item.id);
    console.log(`💾 Guardado en localStorage (${this.attributeName}): ${item.id}`);

    // Actualizar visualmente
    document.querySelectorAll(`#${this.containerId} .cart-selector-option`).forEach(opt => {
      opt.classList.remove("selected");
    });
    labelElement.classList.add("selected");

    // Actualizar atributos del carrito
    this.updateCartAttribute();

    // Callback personalizado si existe
    if (this.onSelect && typeof this.onSelect === 'function') {
      this.onSelect(item);
    }

    // Restaurar scroll si algo (foco del radio, cambios de layout del callback)
    // intentó moverlo. Safari/iOS puede disparar su scroll-into-view nativo de
    // forma diferida y en varios pasos, así que reintentamos en varios
    // instantes en vez de una sola vez.
    const restoreScroll = () => window.scrollTo(0, scrollY);
    requestAnimationFrame(restoreScroll);
    [0, 50, 150, 300, 500].forEach(delay => setTimeout(restoreScroll, delay));
  }

  /**
   * Actualiza el atributo en el carrito de Shopify
   */
  async updateCartAttribute() {
    try {
      const formData = new FormData();
      formData.append(`attributes[${this.attributeName}]`, this.selectedItem.id);
      
      const response = await fetch("/cart/update.js", {
        method: "POST",
        body: formData
      });

      if (!response.ok) {
        throw new Error('Error al actualizar el carrito');
      }

      console.log(`✅ Atributo "${this.attributeName}" actualizado a:`, this.selectedItem.id);
      
      return await response.json();
    } catch (error) {
      console.error(`❌ Error al actualizar ${this.attributeName}:`, error);
      throw error;
    }
  }

  /**
   * Obtiene el item seleccionado actualmente
   */
  getSelected() {
    return this.selectedItem;
  }

  /**
   * Establece la selección programáticamente
   * 🔧 MEJORADO: Actualiza localStorage
   */
  setSelected(itemId) {
    const item = this.items.find(i => i.id === itemId);
    if (item) {
      this.selectedItem = item;
      localStorage.setItem(this.storageKey, item.id);
      this.render();
      this.updateCartAttribute();
    }
  }

  /**
   * 🆕 NUEVO: Limpia la selección almacenada
   * Útil cuando se completa una compra o se vacía el carrito
   */
  clearStorage() {
    localStorage.removeItem(this.storageKey);
    console.log(`🗑️ Storage limpiado (${this.attributeName})`);
  }

  /**
   * 🆕 NUEVO: Sincroniza con el estado actual del carrito
   * Llama esto después de cargar el carrito desde el servidor
   */
  async syncWithCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      if (cart.attributes && cart.attributes[this.attributeName]) {
        const cartValue = cart.attributes[this.attributeName];
        const item = this.items.find(i => i.id === cartValue);
        
        if (item && item.id !== this.selectedItem.id) {
          console.log(`🔄 Sincronizando con carrito: ${cartValue}`);
          this.selectedItem = item;
          localStorage.setItem(this.storageKey, item.id);
          this.render();
        }
      }
    } catch (error) {
      console.log('ℹ️ No se pudo sincronizar con carrito:', error);
    }
  }
}

// Exportar para uso global
window.CartSelector = CartSelector;